import React from "react";
import { useTranslation } from "react-i18next";
import { useLocationSelection } from "../context/LocationSelectionContext";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { Spinner } from "components/ui/spinner";
import LocationSelectionDetails from "../../bookLocation/common/locationSelection/LocationSelectionDetails";
import { LocationSelectDropdown } from "./LocationSelectDropdown";
import { AlertCircle } from "lucide-react";

/**
 * LocationSelectionContent Component
 * ---------------------------------------------------------------
 * Content component for location selection page.
 *
 * @remarks
 * Follows TanStack Query + Context pattern:
 * - URL hydration of location selection via custom hook
 * - Conditional rendering based on selection state
 * - Loading and error states from context
 * - Shows location dropdown and details when location is selected
 *
 * @example
 * ```tsx
 * <LocationSelectionProvider serviceFilter="keys">
 *   <LocationSelectionContent />
 * </LocationSelectionProvider>
 * ```
 */
export function LocationSelectionContent() {
  const { t } = useTranslation();
  const {
    locations,
    selectedLocation,
    isLoadingLocations,
    isErrorLocations,
  } = useLocationSelection();

  // Hydrate location from URL query parameters
  const isHydrated = useLocationHydration();

  // Show spinner while hydrating or loading initial location data
  if (!isHydrated || isLoadingLocations) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
        <Spinner aria-label={t("common.loading")} />
      </div>
    );
  }

  // Show error state if locations failed to load
  if (isErrorLocations) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 px-5" role="alert">
        <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
        <p className="text-center text-lg font-semibold text-slate-900">
          {t("book_location.error_loading_locations")}
        </p>
        <p className="text-center text-sm text-slate-600">
          {t("common.please_try_again")}
        </p>
      </div>
    );
  }

  // Show "no locations" message
  if (!locations || locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-5 py-12">
        <p className="text-center text-slate-700" role="status">
          {t("book_location.no_location")}
        </p>
      </div>
    );
  }


  // Show location selection dropdown and details (no location selected yet)
  return (
    <div className="flex flex-col items-center justify-between px-5 pb-10">
      <div className="w-full max-w-[1000px]">
        <LocationSelectDropdown />
      </div>
      {selectedLocation && (
        <div className="w-full max-w-[1000px]">
          {/* Location Details Section */}
          <LocationSelectionDetails selectedLocation={selectedLocation} />
        </div>
      )}
    </div>
  );
}
